import React from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const formatDateString = (dateStr) => {
    if (!dateStr) return "";
    try {
        const cleanIso = dateStr.includes("T") ? dateStr : dateStr + "T00:00:00";
        const date = new Date(cleanIso);
        if (isNaN(date.getTime())) return dateStr;
        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    } catch (e) { return dateStr; }
};

function EventCard({ event, currentUserId, onDeleteClick }) {

    const eventName = event.title || event.name || "Untitled Event";

    const isCurrentUserAttending = Array.isArray(event.attendees) && event.attendees.some(att => {
        const attId = att.id && typeof att.id === 'object' ? att.id.$oid : att.id;
        return String(attId) === String(currentUserId);
    });

    // 🟢 Dynamic Date Range Formatting Component Engine (Uniform Arrow Edition)
    const datesArray = event.time_table?.dates || [];
    let dateDisplay = "Dates TBA";

    let startRaw = null;
    let endRaw = null;

    if (datesArray.length > 0) {
        startRaw = datesArray[0]?.date;
        endRaw = datesArray[datesArray.length - 1]?.date;
    } else if (event.calendar?.begin_date) {
        startRaw = event.calendar.begin_date.substring(0, 10);
        endRaw = event.calendar.end_date ? event.calendar.end_date.substring(0, 10) : startRaw;
    }

    if (startRaw && endRaw) {
        if (startRaw === endRaw) {
            dateDisplay = formatDateString(startRaw);
        } else {
            try {
                const startDateObj = new Date(startRaw + "T00:00:00");
                const endDateObj = new Date(endRaw + "T00:00:00");

                const startDay = startDateObj.getDate();
                const endDay = endDateObj.getDate();

                const startMonth = startDateObj.toLocaleString('en-US', { month: 'short' });
                const endMonth = endDateObj.toLocaleString('en-US', { month: 'short' });

                const startYear = startDateObj.getFullYear();
                const endYear = endDateObj.getFullYear();

                if (startYear === endYear && startMonth === endMonth) {
                    // ➜ Same Month & Year: "23 ➜ 28 Feb 2025"
                    dateDisplay = `${startDay} ➜ ${endDay} ${startMonth} ${startYear}`;
                } else if (startYear === endYear) {
                    // ➜ Same Year, Different Month: "23 Feb ➜ 2 Mar 2026"
                    dateDisplay = `${startDay} ${startMonth} ➜ ${endDay} ${endMonth} ${startYear}`;
                } else {
                    // ➜ Completely Different Years: "31 Dec 2020 ➜ 1 Jan 2021"
                    dateDisplay = `${startDay} ${startMonth} ${startYear} ➜ ${endDay} ${endMonth} ${endYear}`;
                }
            } catch (e) {
                dateDisplay = `${formatDateString(startRaw)} ➜ ${formatDateString(endRaw)}`;
            }
        }
    }

    const totalAttendees = Array.isArray(event.attendees) ? event.attendees.length : 0;

    let locationDisplay = "Location TBA";
    const primaryLocation = event.lodging?.[0]?.location;

    if (primaryLocation?.city || primaryLocation?.state) {
        locationDisplay = `${primaryLocation.city || ''}, ${primaryLocation.state || ''}`.replace(/^, |, $/, '');
    } else if (event.lodging?.city || event.lodging?.state) {
        locationDisplay = `${event.lodging.city || ''}, ${event.lodging.state || ''}`.replace(/^, |, $/, '');
    }

    const imageBackground = event.cover_image_thumbnail ? `url(${event.cover_image_thumbnail})` : "none";

    const fullRosterTooltip = Array.isArray(event.attendees) && event.attendees.length > 0
        ? event.attendees.map(att => {
            const firstName = (att.fname || '').trim();
            const lastName = (att.lname || '').trim();
            const nickname = (att.nickname || '').trim();

            if (nickname && nickname.toLowerCase() !== firstName.toLowerCase()) {
                return `${firstName} "${nickname}" ${lastName}`;
            }
            return `${firstName} ${lastName}`;
        }).join('\n')
        : "No attendees";

    return (
        <Box
            sx={{
                position: 'relative',
                borderRadius: '20px',
                p: 3,
                height: '220px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                overflow: 'hidden',
                background: '#ffffff',
                border: isCurrentUserAttending ? '1px solid #10b981' : '1px solid #e2e8f0',
                borderLeft: isCurrentUserAttending ? '5px solid #10b981' : '1px solid #e2e8f0',
                boxShadow: isCurrentUserAttending ? '0 4px 20px rgba(16, 185, 129, 0.12)' : '0 4px 16px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    zIndex: -2,
                    backgroundImage: imageBackground,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    transition: 'transform 0.5s ease',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    zIndex: -1,
                    background: event.cover_image_thumbnail
                        ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.75) 0%, rgba(255, 255, 255, 0.95) 60%, #ffffff 100%)'
                        : '#ffffff',
                },
                '&:hover': {
                    transform: 'translateY(-6px)',
                    border: isCurrentUserAttending ? '1px solid #059669' : '1px solid #cbd5e1',
                    borderLeft: isCurrentUserAttending ? '5px solid #10b981' : '1px solid #cbd5e1',
                    boxShadow: isCurrentUserAttending ? '0 12px 30px rgba(16, 185, 129, 0.2)' : '0 12px 24px rgba(0, 0, 0, 0.08)',
                    '&::before': { transform: 'scale(1.04)' }
                }
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, borderRadius: '20px', background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#475569' }}>
                    <LocationOnIcon sx={{ fontSize: '0.9rem', color: '#0284c7' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{locationDisplay}</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isCurrentUserAttending && (
                        <Chip
                            icon={<CheckCircleIcon style={{ color: '#fff', fontSize: '0.9rem' }} />}
                            label="Joined"
                            size="small"
                            sx={{
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                border: '1px solid #34d399', color: '#fff', fontWeight: 700, fontSize: '0.7rem', height: '24px'
                            }}
                        />
                    )}
                    {event.is_template && (
                        <Chip label="Template" size="small" sx={{ background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)', border: '1px solid #c084fc', color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
                    )}
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDeleteClick(event); }} sx={{ background: '#fee2e2', border: '1px solid #fecaca', color: '#ef4444', '&:hover': { background: '#fca5a5', color: '#991b1b' } }}>
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            <Box sx={{ width: '100%', mt: 1, mb: 'auto' }}>
                <Typography variant="h5" component="h3" sx={{ fontWeight: 800, color: '#0f172a', mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                    {eventName}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row-reverse', justifyContent: 'flex-end' }}>
                        {Array.isArray(event.attendees) && event.attendees.slice(0, 4).map((att, i) => {
                            const letter = att.nickname ? att.nickname.trim().charAt(0) : (att.fname ? att.fname.charAt(0) : 'U');
                            return (
                                <Avatar
                                    key={att.id || i}
                                    sx={{
                                        width: 24, height: 24, fontSize: '0.7rem', fontWeight: 700,
                                        border: '2px solid #ffffff', marginLeft: i === 0 ? 0 : '-8px',
                                        background: `linear-gradient(135deg, hue-rotate(${i * 45}deg) #3b82f6 0%, #1d4ed8 100%)`, color: '#fff'
                                    }}
                                >
                                    {letter.toUpperCase()}
                                </Avatar>
                            );
                        })}
                    </Box>

                    {totalAttendees > 0 && (
                        <Tooltip
                            title={
                                <div style={{
                                    whiteSpace: 'pre-line',
                                    padding: '6px 10px',
                                    lineHeight: 1.6,
                                    fontSize: '0.8rem',
                                    fontFamily: 'monospace'
                                }}>
                                    {fullRosterTooltip}
                                </div>
                            }
                            arrow
                            placement="top"
                            enterDelay={150}
                            leaveDelay={100}
                        >
                            <Box sx={{ px: 1, py: 0.2, borderRadius: '12px', background: '#f1f5f9', border: '1px solid #e2e8f0', cursor: 'pointer', '&:hover': { background: '#e2e8f0' } }}>
                                <Typography variant="caption" sx={{ color: '#475569', fontSize: '0.72rem', fontWeight: 600 }}>
                                    {totalAttendees > 4 ? `+${totalAttendees - 4} more` : 'Roster'}
                                </Typography>
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pt: 1.5, borderTop: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#334155' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        {dateDisplay}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.2, py: 0.4, borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#0f172a' }}>
                    <PeopleIcon sx={{ fontSize: '1rem', color: '#64748b' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                        {totalAttendees}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default EventCard;